/**
 * Zustand store for Launch Types + Investment Types catalogs.
 */

import { create } from 'zustand';
import {
  createItemTaxonomy,
  deleteItemTaxonomy,
  listItemTaxonomies,
  setItemTaxonomyActive,
  updateItemTaxonomyLabel,
  type ItemTaxonomyKind,
  type WorkspaceItemTaxonomy,
} from '../../lib/itemTaxonomyService';

interface KindState {
  items: WorkspaceItemTaxonomy[];
  loading: boolean;
  error: string | null;
  loadedForWorkspaceId: string | null;
}

interface ItemTaxonomyStore {
  launch_type: KindState;
  investment_type: KindState;
  fetch: (kind: ItemTaxonomyKind, workspaceId: string) => Promise<void>;
  toggleActive: (kind: ItemTaxonomyKind, id: string, active: boolean) => Promise<void>;
  rename: (
    kind: ItemTaxonomyKind,
    id: string,
    label: string,
    description?: string | null,
  ) => Promise<void>;
  add: (
    kind: ItemTaxonomyKind,
    workspaceId: string,
    label: string,
    description?: string | null,
  ) => Promise<void>;
  remove: (kind: ItemTaxonomyKind, id: string) => Promise<void>;
  labelFor: (kind: ItemTaxonomyKind, value: string | null | undefined) => string;
}

const emptyKind = (): KindState => ({
  items: [],
  loading: false,
  error: null,
  loadedForWorkspaceId: null,
});

export const useItemTaxonomyStore = create<ItemTaxonomyStore>((set, get) => ({
  launch_type: emptyKind(),
  investment_type: emptyKind(),

  fetch: async (kind, workspaceId) => {
    set((s) => ({
      [kind]: { ...s[kind], loading: true, error: null },
    }));
    const { data, error } = await listItemTaxonomies(kind, workspaceId);
    set({
      [kind]: {
        items: data,
        loading: false,
        error,
        loadedForWorkspaceId: workspaceId,
      },
    });
  },

  toggleActive: async (kind, id, active) => {
    const { error } = await setItemTaxonomyActive(kind, id, active);
    if (error) {
      set((s) => ({ [kind]: { ...s[kind], error } }));
      return;
    }
    set((s) => ({
      [kind]: {
        ...s[kind],
        items: s[kind].items.map((t) => (t.id === id ? { ...t, isActive: active } : t)),
      },
    }));
  },

  rename: async (kind, id, label, description) => {
    const { error } = await updateItemTaxonomyLabel(kind, id, label, description);
    if (error) {
      set((s) => ({ [kind]: { ...s[kind], error } }));
      return;
    }
    set((s) => ({
      [kind]: {
        ...s[kind],
        items: s[kind].items.map((t) =>
          t.id === id
            ? { ...t, label, description: description !== undefined ? description : t.description }
            : t,
        ),
      },
    }));
  },

  add: async (kind, workspaceId, label, description = null) => {
    const sortOrder = get()[kind].items.length;
    const { data, error } = await createItemTaxonomy(
      kind,
      workspaceId,
      label,
      description ?? null,
      sortOrder,
    );
    if (error || !data) {
      set((s) => ({ [kind]: { ...s[kind], error: error ?? 'Failed to create' } }));
      return;
    }
    set((s) => ({
      [kind]: { ...s[kind], items: [...s[kind].items, data], error: null },
    }));
  },

  remove: async (kind, id) => {
    const { error } = await deleteItemTaxonomy(kind, id);
    if (error) {
      set((s) => ({ [kind]: { ...s[kind], error } }));
      return;
    }
    set((s) => ({
      [kind]: { ...s[kind], items: s[kind].items.filter((t) => t.id !== id) },
    }));
  },

  labelFor: (kind, value) => {
    if (!value) return '';
    const found = get()[kind].items.find((t) => t.value === value);
    return found?.label ?? value;
  },
}));
